import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    Typography,
    TextField,
    Button,
    Box,
    Grid,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Collapse,
    Autocomplete,
    Chip,
    ListItemButton,
    ListItemIcon,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ImageIcon from '@mui/icons-material/Image';
import PrintIcon from '@mui/icons-material/Print';
import QuestionEditor from './QuestionEditorComponent';
import { useTest } from '../components/TestContext';
import MediaSelector from '../components/MediaSelector';

const TestQuestions = () => {
    const {
        tests: allCategories,
        loading: testsLoading,
        error: testsError,
        fetchTests,
        addTest,
        updateTest,
        deleteTest,
        refreshTests,
    } = useTest();

    const [activeTab, setActiveTab] = useState('tests');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchField, setSearchField] = useState('all');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(() => {
        return activeTab === 'tests' ? 25 : 200;
    });
    const [totalCount, setTotalCount] = useState(0);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [questionPage, setQuestionPage] = useState(1);
    const [questionsPerPage, setQuestionsPerPage] = useState(10);
    const [expandedQuestion, setExpandedQuestion] = useState(null);
    const [questionSearchTerm, setQuestionSearchTerm] = useState('');
    const [filteredQuestions, setFilteredQuestions] = useState([]);
    // const [setAllCategories] = useState([]);
    const DEFAULT_CATEGORY = 'Unassigned';
    const [availableQuestions, setAvailableQuestions] = useState([]); // For existing questions search
    const [searchExistingQuestions, setSearchExistingQuestions] = useState('');
    const [openExistingQuestionsDialog, setOpenExistingQuestionsDialog] = useState(false);
    const [isAddExistingQuestionClicked, setIsAddExistingQuestionClicked] = useState(false);
    const [hasMoreQuestions, setHasMoreQuestions] = useState(true);
    const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);

    useEffect(() => {
        setRowsPerPage(activeTab === 'tests' ? 25 : 200);
        setPage(0); // Reset to first page when switching tabs
        setSearchField('all'); // Reset search field when switching tabs
    }, [activeTab]);

    const fetchItems = useCallback(
        async (search = '', field = 'all') => {
            setLoading(true);
            setError(null);

            const endpoint = activeTab === 'tests' ? '/categories' : '/questions';
            const url = `${process.env.REACT_APP_API_URL}/api${endpoint}`;
            const params = {
                search: search.trim(),
                searchField: field,
                page: page + 1,
                limit: rowsPerPage,
            };

            try {
                const response = await axios.get(url, { params });
                console.log('Fetch items response:', response.data);
                if (response.data && Array.isArray(response.data.items)) {
                    const itemsWithId = response.data.items.map((item) => ({
                        ...item,
                        _id: item._id,
                        name: item.name || item.category,
                    }));
                    setItems(itemsWithId);
                    setTotalCount(response.data.totalCount || 0);
                } else {
                    setItems([]);
                    setTotalCount(0);
                    setError('Received unexpected data format from server');
                }
            } catch (error) {
                console.error(`Error fetching ${activeTab}:`, error);
                setError(`An error occurred while fetching ${activeTab}. Please try again.`);
                setItems([]);
                setTotalCount(0);
            } finally {
                setLoading(false);
            }
        },
        [activeTab, page, rowsPerPage],
    );

    useEffect(() => {
        fetchItems(searchTerm, searchField);
        fetchTests(); // Using the context's fetchTests instead of fetchAllCategories
    }, [activeTab, fetchItems, fetchTests]);


    useEffect(() => {
        if (testsError) {
            setError(testsError);
        }
    }, [testsError]);


    const handleSearch = (event) => {
        event.preventDefault();
        setPage(0);
        fetchItems(searchTerm, searchField);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        fetchItems(searchTerm, searchField);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        fetchItems(searchTerm, searchField);
    };

    const handleEditItem = async (item) => {
        console.log('Editing item:', item);
        let itemId = item._id;
        let endpoint = activeTab === 'tests' ? `/categories/${itemId}` : `/questions/${itemId}`;

        try {
            console.log('Fetching item details from:', `${process.env.REACT_APP_API_URL}/api${endpoint}`);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api${endpoint}`);

            console.log('Fetched item details:', response.data);
            if (response.data) {
                setEditingItem(response.data);
                setOpenDialog(true);
                // Don't automatically fetch questions here
            } else {
                setError(`Failed to fetch ${activeTab === 'tests' ? 'category' : 'question'} details. Please try again.`);
            }
        } catch (error) {
            console.error(`Error fetching ${activeTab === 'tests' ? 'category' : 'question'} details:`, error);
            setError(`Failed to fetch ${activeTab === 'tests' ? 'category' : 'question'} details. Please try again.`);
        }
    };

    // const fetchAllCategories = useCallback(async () => {
    //     try {
    //         const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/categories`, {
    //             params: { limit: 1000 },
    //         });
    //         setAllCategories(response.data.items || []);
    //     } catch (error) {
    //         console.error('Error fetching all categories:', error);
    //         setError('Failed to fetch all categories. Please try again.');
    //     }
    // }, []);

    // useEffect(() => {
    //     fetchItems();
    //     fetchAllCategories();
    // }, [activeTab, fetchItems, fetchAllCategories]);


    const fetchQuestions = async (testId) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/tests/${testId}/questions`, {
                params: { page: 1, limit: questionsPerPage },
            });
            setEditingItem((prevState) => ({
                ...prevState,
                questions: response.data.questions || [],
            }));
        } catch (error) {
            console.error('Error fetching questions:', error);
            setError('Failed to fetch questions. Please try again.');
        }
    };

    const fetchQuestionsForTest = useCallback(
        async (testId) => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/categories/${testId}/questions`, {
                    params: { page: questionPage, limit: questionsPerPage },
                });
                setEditingItem((prevState) => ({
                    ...prevState,
                    questions: response.data.questions || [],
                }));
                setFilteredQuestions(response.data.questions || []);
            } catch (error) {
                console.error('Error fetching questions:', error);
                setError('Failed to fetch questions. Please try again.');
            }
        },
        [questionPage, questionsPerPage],
    );

    const fetchQuestionsForCategory = useCallback(
        async (categoryId, isLoadAll = false) => {
            try {
                setIsLoadingQuestions(true);
                console.log(`Fetching questions for category ${categoryId}, loadAll: ${isLoadAll}`);

                // Ensure we have a valid MongoDB ObjectId
                if (!categoryId || typeof categoryId !== 'string' || !categoryId.match(/^[0-9a-fA-F]{24}$/)) {
                    console.error('Invalid category ID:', categoryId);
                    setError('Invalid category ID');
                    return;
                }

                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/categories/${categoryId}/questions`, {
                    params: {
                        page: questionPage,
                        limit: questionsPerPage,
                        loadAll: isLoadAll,
                    },
                });

                if (response.data && response.data.questions) {
                    const newQuestions = response.data.questions;

                    setEditingItem((prevState) => {
                        // Only update if we still have the same category selected
                        if (prevState?._id !== categoryId) return prevState;

                        return {
                            ...prevState,
                            questions: isLoadAll || questionPage === 1 ? newQuestions : [...(prevState.questions || []), ...newQuestions],
                            question_ids: [...new Set([...(prevState.question_ids || []), ...newQuestions.map((q) => q._id)])],
                        };
                    });

                    setFilteredQuestions((prevFiltered) =>
                        isLoadAll || questionPage === 1 ? newQuestions : [...prevFiltered, ...newQuestions],
                    );

                    setHasMoreQuestions(response.data.hasMore);
                }
            } catch (error) {
                console.error('Error fetching questions:', error);
                setError(error.response?.data?.message || 'Failed to fetch questions. Please try again.');
            } finally {
                setIsLoadingQuestions(false);
            }
        },
        [questionPage, questionsPerPage],
    );

    // Update the Load Questions and Load All Questions button handlers
    const loadAllQuestions = async () => {
        if (editingItem?._id && activeTab === 'tests') {
            setQuestionPage(1); // Reset page number first
            await fetchQuestionsForCategory(editingItem._id, true);
        }
    };

    const loadMoreQuestions = async () => {
        if (editingItem?._id && activeTab === 'tests' && !isLoadingQuestions && hasMoreQuestions) {
            setQuestionPage((prev) => prev + 1);
            await fetchQuestionsForCategory(editingItem._id);
        }
    };
    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingItem(null);
        setExpandedQuestion(null);
    };

    const handleSaveItem = async () => {
        try {
            console.log('Starting save process...');
            console.log('Current active tab:', activeTab);
            console.log('Saving item:', editingItem);

            let response;
            if (activeTab === 'tests') {
                // When saving questions from within a test
                if (editingItem.questions && editingItem.questions.length > 0) {
                    // First save any new questions
                    const questionPromises = editingItem.questions
                        .filter((q) => !q._id) // Only new questions
                        .map(async (question) => {
                            const questionData = {
                                ...question,
                                category_ids: [editingItem._id], // Associate with current test
                            };
                            const questionResponse = await axios.post(`${process.env.REACT_APP_API_URL}/api/questions`, questionData);
                            return questionResponse.data._id;
                        });

                    const newQuestionIds = await Promise.all(questionPromises);

                    // Get existing question IDs
                    // const existingQuestionIds = editingItem.questions
                    //     .filter((q) => q._id)
                    //     .map((q) => q._id);
                    const existingQuestionIds = [...editingItem.question_ids];

                    // Combine all question IDs
                    const allQuestionIds = [...existingQuestionIds, ...newQuestionIds];

                    console.log('combine data all questions', allQuestionIds);

                    // Prepare category/test data with updated question_ids
                    const categoryData = {
                        name: editingItem.name || editingItem.category,
                        question_ids: allQuestionIds,
                        description: editingItem.description || '',
                    };

                    if (editingItem._id) {
                        response = await axios.put(`${process.env.REACT_APP_API_URL}/api/categories/${editingItem._id}`, categoryData);
                    } else {
                        response = await axios.post(`${process.env.REACT_APP_API_URL}/api/categories`, categoryData);
                    }
                } else {
                    // Regular test save without questions
                    const categoryData = {
                        name: editingItem.name || editingItem.category,
                        question_ids: editingItem.question_ids || [],
                        description: editingItem.description || '',
                    };

                    if (editingItem._id) {
                        response = await axios.put(`${process.env.REACT_APP_API_URL}/api/categories/${editingItem._id}`, categoryData);
                    } else {
                        response = await axios.post(`${process.env.REACT_APP_API_URL}/api/categories`, categoryData);
                    }
                }
            } else {
                // Handle regular question creation/update
                if (!editingItem.question_text || !editingItem.answers.answer_a) {
                    throw new Error('Question text and at least one answer are required');
                }

                const questionData = { ...editingItem };
                delete questionData._id;

                if (editingItem._id) {
                    response = await axios.put(`${process.env.REACT_APP_API_URL}/api/questions/${editingItem._id}`, questionData);
                } else {
                    response = await axios.post(`${process.env.REACT_APP_API_URL}/api/questions`, questionData);
                }
            }
            console.log('Save process complete');
            handleCloseDialog();
            await fetchItems(searchTerm, searchField);
        } catch (error) {
            console.error('Error saving item:', error);
            setError(`An error occurred while saving: ${error.response?.data?.message || error.message}`);
        }
    };
    // const handleSaveItem = async () => {
    //     try {
    //         console.log('Saving item:', editingItem);

    //         let itemToSave = { ...editingItem };
    //         let response;

    //         if (activeTab === 'questions') {
    //             // Ensure assignedTests is an array of test IDs
    //             itemToSave.assignedTests = (itemToSave.assignedTests || []).map((test) => test._id || test);

    //             // Remove _id from the request body
    //             const { _id, ...itemWithoutId } = itemToSave;

    //             if (_id) {
    //                 // Update existing question
    //                 response = await axios.put(`${process.env.REACT_APP_API_URL}/api/questions/${_id}`, itemWithoutId);
    //             } else {
    //                 // Create new question
    //                 response = await axios.post(`${process.env.REACT_APP_API_URL}/api/questions`, itemWithoutId);
    //             }
    //         } else {
    //             // Handling for tests
    //             if (itemToSave._id) {
    //                 // Update existing test
    //                 response = await axios.put(`${process.env.REACT_APP_API_URL}/api/categories/${itemToSave._id}`, itemToSave);
    //             } else {
    //                 // Create new test
    //                 response = await axios.post(`${process.env.REACT_APP_API_URL}/api/categories`, itemToSave);
    //             }
    //         }

    //         console.log('Server response:', response.data);

    //         // Update the editingItem with the response data
    //         setEditingItem(response.data);

    //         handleCloseDialog();
    //         await fetchItems(searchTerm);
    //         console.log('Items after fetch:', items);
    //     } catch (error) {
    //         console.error('Error saving item:', error.response?.data || error.message);
    //         setError(`An error occurred while saving: ${error.response?.data?.message || error.message}`);
    //     }
    // };

    const handleDeleteItem = async (item) => {
        if (window.confirm('Are you sure you want to delete this item?')) {
            try {
                if (activeTab === 'tests') {
                    await axios.delete(`${process.env.REACT_APP_API_URL}/api/tests/${item._id}`);
                } else {
                    await axios.delete(`${process.env.REACT_APP_API_URL}/api/questions/${item._id}`);
                }
                fetchItems(searchTerm, searchField);
            } catch (error) {
                console.error('Error deleting item:', error);
                setError('An error occurred while deleting. Please try again.');
            }
        }
    };

    const handleAddNewItem = () => {
        if (activeTab === 'tests') {
            setEditingItem({
                name: '',
                category: '',
                description: '',
                question_ids: [], // Add empty question_ids array
                questions: [], // Keep empty questions array for UI
            });
        } else {
            setEditingItem({
                question_text: '',
                answers: {
                    answer_a: '',
                    answer_b: '',
                    answer_c: '',
                    answer_d: '',
                },
                correct_answer: '',
                solution: '',
                reference: '',
                category_ids: [],
                image: { url: '', alt_text: '' },
            });
        }
        setOpenDialog(true);
    };

    const handleQuestionClick = (index) => {
        setExpandedQuestion(expandedQuestion === index ? null : index);
    };

    const handleImageUpload = async (event, questionIndex) => {
        const file = event.target.files[0];
        if (file) {
            const formData = new FormData();
            formData.append('image', file);

            try {
                const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/upload/upload-image`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                });

                console.log('Image upload response:', response.data);

                setEditingItem((prevItem) => {
                    if (Array.isArray(prevItem.questions)) {
                        const updatedQuestions = [...prevItem.questions];
                        updatedQuestions[questionIndex] = {
                            ...updatedQuestions[questionIndex],
                            image: {
                                url: response.data.url,
                                alt_text: file.name,
                            },
                        };
                        return { ...prevItem, questions: updatedQuestions };
                    } else {
                        return {
                            ...prevItem,
                            image: {
                                url: response.data.url,
                                alt_text: file.name,
                            },
                        };
                    }
                });
            } catch (error) {
                console.error('Error uploading image:', error);
                setError('Failed to upload image. Please try again.');
            }
        }
    };

    const handleRemoveImage = (questionIndex) => {
        setEditingItem((prevItem) => {
            if (Array.isArray(prevItem.questions)) {
                const updatedQuestions = [...prevItem.questions];
                updatedQuestions[questionIndex] = {
                    ...updatedQuestions[questionIndex],
                    image: null,
                };
                return { ...prevItem, questions: updatedQuestions };
            } else {
                return {
                    ...prevItem,
                    image: null,
                };
            }
        });
    };

    // refacoring the print function
    const pdfPrinter = function (response, printType) {
        // Check if the response is JSON (error) or blob (PDF)
        const contentType = response.headers['content-type'];
        if (contentType && contentType.indexOf('application/json') !== -1) {
            // It's an error response
            const reader = new FileReader();
            reader.onload = () => {
                const errorData = JSON.parse(reader.result);
                setError(errorData.error || 'An error occurred while generating the PDF.');
            };
            reader.readAsText(response.data);
        } else {
            // It's a PDF
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `searched_${printType}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    };

    const handlePrint = async (printType) => {
        if (editingItem && activeTab === 'tests') {
            try {
                console.log('Editing item:', editingItem);

                // Use _id if available, otherwise use category
                const testIdentifier = editingItem._id || editingItem.category;

                if (!testIdentifier) {
                    console.error('Invalid test identifier');
                    setError('Invalid test identifier. Please try again.');
                    return;
                }
                console.log('Sending print request for test:', testIdentifier);

                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/print/print-test/${encodeURIComponent(testIdentifier)}`,
                    {
                        params: { type: printType },
                        responseType: 'blob',
                    },
                );

                //invoking pdf printer funciton
                pdfPrinter(response, printType);

                // // Check if the response is JSON (error) or blob (PDF)
                // const contentType = response.headers['content-type'];
                // if (
                //     contentType &&
                //     contentType.indexOf('application/json') !== -1
                // ) {
                //     // It's an error response
                //     const reader = new FileReader();
                //     reader.onload = () => {
                //         const errorData = JSON.parse(reader.result);
                //         setError(
                //             errorData.error ||
                //                 'An error occurred while generating the PDF.',
                //         );
                //     };
                //     reader.readAsText(response.data);
                // } else {
                //     // It's a PDF
                //     const url = window.URL.createObjectURL(
                //         new Blob([response.data]),
                //     );
                //     const link = document.createElement('a');
                //     link.href = url;
                //     link.setAttribute(
                //         'download',
                //         `${editingItem.category}_${printType}.pdf`,
                //     );
                //     document.body.appendChild(link);
                //     link.click();
                //     link.remove();
                // }
            } catch (error) {
                console.error('Error generating print version:', error);
                let errorMessage = 'Failed to generate print version. ';
                if (error.response) {
                    console.error('Full error response:', error.response);
                    errorMessage += error.response.data.error || error.response.data.details || '';
                }
                setError(errorMessage);
            }
        }
    };

    //print questions
    const handlePrintQuestions = async (printType) => {
        //send request only if there is search term otherwise need to print all(21k) questions
        if (activeTab === 'questions' && searchTerm) {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/print/print-questions/?search=${searchTerm}`, {
                    params: { type: printType },
                    responseType: 'blob',
                });
                console.log('from print question', response, response.data);
                //invoking pdf printer funciton
                pdfPrinter(response, printType);
            } catch (error) {
                console.error('Error generating print version:', error);
                let errorMessage = 'Failed to generate print version. ';
                if (error.response) {
                    console.error('Full error response:', error.response);
                    errorMessage += error.response.data.error || error.response.data.details || '';
                }
                setError(errorMessage);
            }
        } else {
            console.log('Search bar is empty?');
        }
    };

    const handleQuestionSearch = (e) => {
        const searchTerm = e.target.value;
        setQuestionSearchTerm(searchTerm);

        if (editingItem && editingItem.questions) {
            const filtered = editingItem.questions.filter((question) =>
                question.question_text.toLowerCase().includes(searchTerm.toLowerCase()),
            );
            setFilteredQuestions(filtered);
        }
    };

    const handleAddQuestion = useCallback(() => {
        const newQuestion = {
            question_text: '',
            answers: { answer_a: '', answer_b: '', answer_c: '', answer_d: '' },
            correct_answer: '',
            solution: '',
            reference: '',
            image: { url: '', alt_text: '' },
            category_ids: activeTab === 'tests' && editingItem?._id ? [editingItem._id] : [],
        };

        if (activeTab === 'tests') {
            setEditingItem((prevState) => ({
                ...prevState,
                questions: [...(prevState.questions || []), newQuestion],
            }));
            setExpandedQuestion((prevState) => {
                const questions = editingItem?.questions || [];
                return questions.length;
            });
        } else {
            setEditingItem(newQuestion);
        }
    }, [editingItem, activeTab]);

    const handleSearchExistingQuestions = async (searchTerm) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/questions`, {
                params: {
                    search: searchTerm,
                    limit: 10,
                },
            });
            setAvailableQuestions(response.data.items || []);
        } catch (error) {
            console.error('Error searching questions:', error);
            setError('Failed to search questions');
        }
    };

    const handleAddExistingSaveItem = async function () {
        setIsAddExistingQuestionClicked(false);
        handleCloseDialog();
        await fetchItems(searchTerm, searchField);
    };

    const handleAddExistingQuestion = async (question) => {
        try {
            if (!editingItem?._id) return;

            // Add the test to the question's category_ids
            const updatedQuestion = {
                ...question,
                category_ids: [...(question.category_ids || []), editingItem._id],
            };

            // Update the question
            await axios.put(`${process.env.REACT_APP_API_URL}/api/questions/${question._id}`, updatedQuestion);

            console.log('from add existing quesiton:', updatedQuestion);

            // Refresh the questions list
            await fetchQuestionsForCategory(editingItem._id);
            setOpenExistingQuestionsDialog(false);
            setIsAddExistingQuestionClicked(true);
        } catch (error) {
            console.error('Error adding existing question:', error);
            setError('Failed to add question to test');
        }
    };

    const handleQuestionChange = useCallback(
        (updatedQuestion) => {
            setEditingItem((prevState) => {
                if (!prevState) return prevState;

                if (activeTab === 'tests') {
                    const questions = prevState.questions || [];
                    const questionIndex = questions.findIndex((q) => q._id === updatedQuestion._id);
                    if (questionIndex === -1) {
                        // This is a new question
                        return {
                            ...prevState,
                            questions: [...questions, updatedQuestion],
                        };
                    }
                    const updatedQuestions = [...questions];
                    updatedQuestions[questionIndex] = updatedQuestion;
                    if (JSON.stringify(updatedQuestions) !== JSON.stringify(questions)) {
                        console.log('handleQuestionChange:', updatedQuestion);
                        // Update the question
                        setIsAddExistingQuestionClicked(true);
                        (async () => {
                            try {
                                const updateCat = await axios.put(
                                    `${process.env.REACT_APP_API_URL}/api/questions/${updatedQuestion._id}`,
                                    updatedQuestion,
                                );
                                console.log('category updated:', updateCat);
                            } catch (err) {
                                console.log('error while updating quesiton category:', err.message);
                            }
                        })();
                        return {
                            ...prevState,
                            questions: updatedQuestions,
                        };
                    }
                } else {
                    if (JSON.stringify(updatedQuestion) !== JSON.stringify(prevState)) {
                        return updatedQuestion;
                    }
                }
                return prevState;
            });
        },
        [activeTab, isAddExistingQuestionClicked],
    );

    const memoizedAllCategories = useMemo(() => allCategories, [allCategories]);

    const renderTableBody = () => (
        <TableBody>
            {items.map((item) => (
                <TableRow key={item._id || item.id || item.name}>
                    <TableCell>{activeTab === 'tests' ? item.name || item.category : item.question_text}</TableCell>
                    {activeTab === 'tests' && (
                        <TableCell align="right">{item.questionCount || (item.question_ids && item.question_ids.length) || 0}</TableCell>
                    )}
                    <TableCell align="right">
                        <IconButton onClick={() => handleEditItem(item)}>
                            <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteItem(item)}>
                            <DeleteIcon />
                        </IconButton>
                    </TableCell>
                </TableRow>
            ))}
        </TableBody>
    );

    const renderDialogContent = () => {
        if (!editingItem) return null;

        if (activeTab === 'tests') {
            return (
                <>
                    <TextField
                        fullWidth
                        label="Category Name"
                        value={editingItem?.name || editingItem?.category || ''}
                        onChange={(e) =>
                            setEditingItem((prev) => ({
                                ...prev,
                                name: e.target.value,
                                category: e.target.value,
                            }))
                        }
                        margin="normal"
                        required
                        helperText="This will be the name of your test category"
                    />
                    {editingItem._id && ( // Only show questions section for existing tests
                        <>
                            {/* Question Management Controls - Moved to top */}
                            <Box sx={{ mt: 3, mb: 2 }}>
                                {/* Search Box */}
                                <TextField
                                    fullWidth
                                    label="Search Questions"
                                    value={questionSearchTerm}
                                    onChange={handleQuestionSearch}
                                    margin="normal"
                                />

                                {/* Question Management Buttons */}
                                <Box sx={{ mt: 2, mb: 2, display: 'flex', gap: 2 }}>
                                    <Button variant="outlined" onClick={() => setOpenExistingQuestionsDialog(true)} sx={{ flex: 1 }}>
                                        Add Existing Question
                                    </Button>
                                    <Button variant="outlined" onClick={handleAddQuestion} sx={{ flex: 1 }}>
                                        Add New Question
                                    </Button>
                                </Box>

                                {/* Print Buttons */}
                                <Box sx={{ mt: 2, mb: 3, display: 'flex', justifyContent: 'space-between' }}>
                                    <Button variant="contained" startIcon={<PrintIcon />} onClick={() => handlePrint('questions')}>
                                        Print Questions
                                    </Button>
                                    <Button variant="contained" startIcon={<PrintIcon />} onClick={() => handlePrint('questions-answers')}>
                                        Print Questions & Answers
                                    </Button>
                                    <Button variant="contained" startIcon={<PrintIcon />} onClick={() => handlePrint('answers')}>
                                        Print Answers
                                    </Button>
                                </Box>
                            </Box>

                            {editingItem.questions && editingItem.questions.length > 0 ? (
                                <>
                                    <Typography variant="h6">Questions</Typography>
                                    <List>
                                        {(questionSearchTerm ? filteredQuestions : editingItem.questions).map((question, index) => (
                                            <ListItem key={question._id || index}>
                                                <ListItemButton onClick={() => handleQuestionClick(index)}>
                                                    <ListItemText primary={question.question_text || `Question ${index + 1}`} />
                                                    {expandedQuestion === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </ListItemButton>
                                                <Collapse in={expandedQuestion === index} timeout="auto" unmountOnExit>
                                                    <QuestionEditor
                                                        question={question}
                                                        onQuestionChange={handleQuestionChange}
                                                        onImageUpload={(event) => handleImageUpload(event, index)}
                                                        onRemoveImage={() => handleRemoveImage(index)}
                                                        allTests={Array.isArray(allCategories) ? allCategories : []}
                                                    />
                                                </Collapse>
                                            </ListItem>
                                        ))}
                                    </List>
                                </>
                            ) : (
                                <Typography>No questions loaded. Add questions using the buttons above.</Typography>
                            )}
                        </>
                    )}
                </>
            );
        } else {
            return (
                <QuestionEditor
                    question={editingItem}
                    onQuestionChange={handleQuestionChange}
                    onImageUpload={handleImageUpload}
                    onRemoveImage={handleRemoveImage}
                    allTests={Array.isArray(allCategories) ? allCategories : []}
                    defaultTestId={activeTab === 'tests' ? editingItem?._id : null}
                />
            );
        }
    };

    const renderExistingQuestionsDialog = () => (
        <Dialog open={openExistingQuestionsDialog} onClose={() => setOpenExistingQuestionsDialog(false)} maxWidth="md" fullWidth>
            <DialogTitle>Add Existing Question</DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth
                    label="Search Questions"
                    value={searchExistingQuestions}
                    onChange={(e) => {
                        setSearchExistingQuestions(e.target.value);
                        handleSearchExistingQuestions(e.target.value);
                    }}
                    margin="normal"
                />
                <List>
                    {availableQuestions.map((question) => (
                        <ListItem key={question._id} button onClick={() => handleAddExistingQuestion(question)}>
                            <ListItemText primary={question.question_text} />
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenExistingQuestionsDialog(false)}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <Box sx={{ maxWidth: 1200, margin: 'auto', padding: 2 }}>
            <Typography variant="h4" gutterBottom>
                {activeTab === 'tests' ? 'Test' : 'Question'} Management
            </Typography>
            <Typography variant="h5" gutterBottom>
                Use The Questions Tab To Edit Questions &amp; Assign To Tests
            </Typography>

            <Box sx={{ mb: 2 }}>
                <Button variant={activeTab === 'tests' ? 'contained' : 'outlined'} onClick={() => setActiveTab('tests')} sx={{ mr: 1 }}>
                    Tests
                </Button>
                <Button variant={activeTab === 'questions' ? 'contained' : 'outlined'} onClick={() => setActiveTab('questions')}>
                    Questions
                </Button>
            </Box>

            <form onSubmit={handleSearch}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={activeTab === 'questions' ? 6 : 12}>
                        <TextField
                            fullWidth
                            label={`Search ${activeTab}`}
                            variant="outlined"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </Grid>
                    {activeTab === 'questions' && (
                        <Grid item xs={3}>
                            <FormControl fullWidth>
                                <InputLabel>Search Field</InputLabel>
                                <Select
                                    value={searchField}
                                    label="Search Field"
                                    onChange={(e) => setSearchField(e.target.value)}
                                >
                                    <MenuItem value="all">All Fields</MenuItem>
                                    <MenuItem value="question_text">Question Text</MenuItem>
                                    <MenuItem value="reference">Reference</MenuItem>
                                    <MenuItem value="solution">Solution</MenuItem>
                                    <MenuItem value="correct_answer">Correct Answer</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                    <Grid item>
                        <Button type="submit" variant="contained" startIcon={<SearchIcon />}>
                            Search
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={handleAddNewItem}>
                            Add New
                        </Button>
                    </Grid>
                </Grid>
            </form>

            {loading ? (
                <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 2 }} />
            ) : items.length === 0 ? (
                <Typography sx={{ mt: 2 }}>No items found. Try adjusting your search or adding new items.</Typography>
            ) : (
                <>
                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{activeTab === 'tests' ? 'Test Name' : 'Question'}</TableCell>
                                    {activeTab === 'tests' && <TableCell align="right">Question Count</TableCell>}
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            {renderTableBody()}
                        </Table>
                    </TableContainer>
                    {/* print questions  */}
                    {activeTab === 'questions' && (
                        <Box
                            sx={{
                                mt: 2,
                                display: 'flex',
                                gap: '20px',
                            }}
                        >
                            <Button variant="contained" startIcon={<PrintIcon />} onClick={() => handlePrintQuestions('questions')}>
                                Print Questions
                            </Button>
                            <Button variant="contained" startIcon={<PrintIcon />} onClick={() => handlePrintQuestions('questions-answers')}>
                                Print Questions & Answers
                            </Button>
                            <Button variant="contained" startIcon={<PrintIcon />} onClick={() => handlePrintQuestions('answers')}>
                                Print Answers
                            </Button>
                        </Box>
                    )}

                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                </>
            )}

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 1,
                    borderBottom: editingItem && activeTab === 'tests' && editingItem.name ? '1px solid rgba(0, 0, 0, 0.12)' : 'none'
                }}>
                    <Typography variant="h6">
                        {editingItem && (activeTab === 'tests' ? editingItem.name : editingItem._id) ? 'Edit' : 'Add New'}{' '}
                        {activeTab === 'tests' ? 'Test' : 'Question'}
                    </Typography>
                    {editingItem && activeTab === 'tests' && editingItem.name && (
                        <Typography variant="subtitle1" color="primary" fontWeight="medium" sx={{ mb: 1 }}>
                            Test: {editingItem.name}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>{renderDialogContent()}</DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={isAddExistingQuestionClicked ? handleAddExistingSaveItem : handleSaveItem}>Save</Button>
                </DialogActions>
            </Dialog>
            {renderExistingQuestionsDialog()}
        </Box>
    );
};

export default TestQuestions;
